<template>
  <section id="talent" class="py-12 bg-white">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-6">
        <h2 class="text-[clamp(1.5rem,3vw,2.5rem)] font-bold text-dark">人才入库</h2>
        <p class="mt-4 text-gray-medium max-w-2xl mx-auto">加入人才库，展示您的专业技能和资质，获得更多职业发展机会</p>
      </div>

      <!-- 搜索栏 -->
      <div class="max-w-xl mx-auto mb-6">
        <div class="relative">
          <input 
            v-model="searchKeyword"
            @keyup.enter="handleSearch"
            type="text" 
            placeholder="搜索人才姓名..." 
            class="w-full px-4 py-3 pl-12 pr-20 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 shadow-sm"
          >
          <i class="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <button 
            @click="handleSearch"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 px-5 py-1.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 transition-all duration-300"
          >
            搜索
          </button>
        </div>
      </div>

      <!-- 错误提示 -->
      <div v-if="error" class="text-center py-6 mb-8">
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
          <div class="flex items-center justify-center mb-2">
            <i class="fas fa-exclamation-triangle text-yellow-500 text-lg mr-2"></i>
            <h3 class="text-sm font-semibold text-yellow-800">离线模式</h3>
          </div>
          <p class="text-yellow-700 text-xs">{{ error }}</p>
        </div>
      </div>

      <!-- 人才库展示 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="talent in talents" 
          :key="talent.id"
          class="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-custom border border-gray-100 group cursor-pointer"
          @click="viewTalentDetail(talent.id)"
        >
          <div class="p-6">
            <!-- 头像和基本信息 -->
            <div class="flex flex-col items-center mb-4">
              <img 
                :src="talent.avatar ? $getImageUrl(talent.avatar) : require('@/assets/default-avatar.jpg')" 
                :alt="talent.name" 
                class="w-16 h-16 rounded-full object-cover border-2 border-gray-200 flex-shrink-0 mb-3"
              >
              <div class="text-center w-full">
                <h3 class="font-bold text-lg text-gray-900 group-hover:text-primary transition-custom">{{ talent.name }}</h3>
                <p class="text-gray-600 text-sm mt-0.5">{{ talent.profession }}</p>
              </div>
            </div>

            <!-- 专业技能 -->
            <div class="mb-3">
              <h4 class="text-xs font-medium text-gray-500 mb-2">专业技能</h4>
              <div class="flex flex-wrap gap-1.5">
                <span 
                  v-for="(skill, index) in formatSkills(talent.skills).slice(0, 4)" 
                  :key="index"
                  class="bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full"
                >
                  {{ skill }}
                </span>
                <span 
                  v-if="formatSkills(talent.skills).length > 4" 
                  class="text-xs text-gray-500 px-2 py-1"
                >
                  +{{ formatSkills(talent.skills).length - 4 }}
                </span>
              </div>
            </div>

            <!-- 所持证书 -->
            <div class="mb-4">
              <h4 class="text-xs font-medium text-gray-500 mb-2">所持证书</h4>
              <div class="flex flex-wrap gap-1.5">
                <span 
                  v-for="(cert, index) in formatCertificates(talent.certificates).slice(0, 2)" 
                  :key="index"
                  class="bg-blue-50 text-blue-600 text-xs px-2.5 py-1 rounded-full border border-blue-100"
                >
                  {{ cert }}
                </span>
                <span 
                  v-if="formatCertificates(talent.certificates).length > 2" 
                  class="text-xs text-gray-500 px-2 py-1"
                >
                  +{{ formatCertificates(talent.certificates).length - 2 }}
                </span>
              </div>
            </div>

            <!-- 底部信息 -->
            <div class="pt-3 border-t border-gray-100 flex justify-between items-center">
              <span class="text-xs text-gray-500">
                <i class="far fa-clock mr-1"></i>
                {{ formatTime(talent.updatetime) }}更新
              </span>
              <button 
                @click.stop="viewTalentDetail(talent.id)" 
                class="text-primary font-medium text-sm hover:underline transition-custom"
              >
                查看详情
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部按钮区域 -->
      <div class="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
        <button @click="$emit('open-talent-modal')" class="px-8 py-3 bg-secondary text-white font-medium rounded-lg hover:bg-secondary/90 transition-custom shadow-sm">
          <i class="fas fa-user-plus mr-2"></i> 立即申请入库
        </button>
        <button @click="$router.push('/talent')" class="px-6 py-3 bg-white border border-gray-light rounded-lg text-gray-dark font-medium hover:bg-light transition-custom flex items-center">
          <span>查看更多人才</span>
          <i class="fas fa-arrow-right ml-2 text-gray-medium"></i>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { talentAPI } from '@/api/services'

export default {
  name: 'TalentSection',
  data() {
    return {
      talents: [],
      error: null,
      searchKeyword: '' // 搜索关键词
    }
  },
  async mounted() {
    await this.loadTalents()
  },
  methods: {
    async loadTalents() {
      this.error = null
      
      try {
        const params = { 
          page: 1, 
          limit: 3 
        }

        // 如果有搜索关键词，添加到参数中
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim()
        }

        const response = await talentAPI.getTalents(params)
        this.talents = response.data || response
      } catch (error) {
        console.error('加载人才列表失败:', error)
        this.error = error.msg || error.message || '加载人才列表失败，请稍后重试'
        this.talents = []
      }
    },
    
    // 处理搜索
    handleSearch() {
      this.loadTalents()
    },
    
    // 清除搜索
    clearSearch() {
      this.searchKeyword = ''
      this.loadTalents()
    },

    viewTalentDetail(talentId) {
      this.$router.push(`/talent/${talentId}`)
    },

    // 格式化技能（处理字符串或数组）
    formatSkills(skills) {
      if (Array.isArray(skills)) {
        return skills
      }
      return skills ? skills.split(',').map(s => s.trim()) : []
    },

    // 格式化证书（处理字符串或数组）
    formatCertificates(certificates) {
      if (Array.isArray(certificates)) {
        return certificates
      }
      return certificates ? certificates.split(',').map(c => c.trim()) : []
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      // 如果是秒级时间戳（10位数），转换为毫秒级
      const timestamp = String(time).length === 10 ? time * 1000 : time
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style lang="less" scoped>
.transition-custom {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 